import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Import the CSS module
import styles from './support.module.css';
import { BASE_URL } from '../utils/config';

export default function Support() {
    const { t } = useTranslation('support');

    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        mobile: '',
        issue: '',
        description: '',
        captchaInput: '',
    });

    const [captcha, setCaptcha] = useState({
        num1: 0,
        num2: 0,
        answer: 0,
    });
    const [captchaError, setCaptchaError] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submittedData, setSubmittedData] = useState(null); // Store submitted form data
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        generateCaptcha();
    }, []);

    const generateCaptcha = () => {
        const n1 = Math.floor(Math.random() * 10) + 1;
        const n2 = Math.floor(Math.random() * 10) + 1;
        setCaptcha({
            num1: n1,
            num2: n2,
            answer: n1 + n2,
        });
        setFormData(prev => ({ ...prev, captchaInput: '' }));
        setCaptchaError(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (submitAttempted) {
            if (name === 'captchaInput') {
                setCaptchaError(false);
            }
        }
    };

    const validateForm = () => {
        const { name, surname, email, mobile, issue, description, captchaInput } = formData;
        // Check for empty required fields
        if (!name || !surname || !email || !mobile || !issue || !description) {
            return false;
        }
        // Validate email format (basic check)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return false;
        }
        // Validate captcha
        if (parseInt(captchaInput) !== captcha.answer) {
            setCaptchaError(true);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);
        setSubmitError('');

        if (validateForm()) {
            setIsLoading(true);
            
            try {
                // Prepare the data for API submission (excluding captcha)
                const { captchaInput, ...apiData } = formData;
                
                const response = await fetch(`${BASE_URL}/support/guest`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(apiData)
                });

                if (response.ok) {
                    console.log("Support request submitted successfully");
                    // Store submitted data before resetting
                    setSubmittedData({ ...apiData });
                    setFormSubmitted(true);
                    
                    // Reset form fields
                    setFormData({
                        name: '',
                        surname: '',
                        email: '',
                        mobile: '',
                        issue: '',
                        description: '',
                        captchaInput: '',
                    });
                    generateCaptcha(); // Generate a new CAPTCHA
                    setSubmitAttempted(false); // Reset submit attempt state
                    setCaptchaError(false); // Clear CAPTCHA error on success
                } else {
                    const errorData = await response.json();
                    setSubmitError(errorData.message || 'Failed to submit support request. Please try again.');
                }
            } catch (error) {
                console.error('Error submitting support request:', error);
                setSubmitError('Network error. Please check your connection and try again.');
            } finally {
                setIsLoading(false);
            }
        } else {
            console.log("Form validation failed.");
            // Captcha error is already set by validateForm
        }
    };

    // Use t() for issue options labels
    const issueOptions = [
        { value: '', label: t('support.issue_options.select', 'Select an issue') },
        { value: 'technical', label: t('support.issue_options.technical', 'Technical issue') },
        { value: 'payment', label: t('support.issue_options.payment', 'Payment issue') },
        { value: 'changes', label: t('support.issue_options.changes', 'Changes needed') },
        { value: 'suggestion', label: t('support.issue_options.suggestion', 'Improvement suggestion') },
        { value: 'advertisement', label: t('support.issue_options.advertisement', 'Advertisement request') },
    ];

    return (
        <div className={styles.formContainer}>
            <h1 className={styles.mainHeading}>{t('support.title', 'Support Form')}</h1>
            <p className={styles.introText}>
                <em>{t('support.intro_text', 'Please fill out the form below for any support inquiries.')}</em>
            </p>

            {formSubmitted && submittedData && (
                <div className={styles.success}>
                    <p style={{ marginBottom: '15px', fontWeight: 'bold', fontSize: '18px' }}>
                        {t('support.request_received', 'Your Request Has been Received')}
                    </p>
                    <p style={{ marginBottom: '15px' }}>
                        {t('support.copy_of_response', 'This is the copy of your response:')}
                    </p>
                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '15px', 
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        marginTop: '10px'
                    }}>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.name_label', 'Name')}:</strong> {submittedData.name}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.surname_label', 'Surname')}:</strong> {submittedData.surname}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.email_label', 'E-mail')}:</strong> {submittedData.email}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.mobile_label', 'Mobile Nr')}:</strong> {submittedData.mobile}
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.issue_label', 'Issue')}:</strong> {
                                issueOptions.find(opt => opt.value === submittedData.issue)?.label || submittedData.issue
                            }
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <strong>{t('support.fields.description_label', 'Description')}:</strong>
                            <div style={{ marginTop: '5px', whiteSpace: 'pre-wrap' }}>
                                {submittedData.description}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {submitError && (
                <div className={styles.error}>
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
                {/* FIRST ROW: NAME AND SURNAME */}
                <div className={styles.twoColumnRow}>
                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="name">
                            {t('support.fields.name_label', 'Name')}<span className={styles.required}>*</span>:
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={styles.input}
                            required
                            placeholder={t('support.fields.name_placeholder', 'Enter your name')}
                        />
                        {submitAttempted && !formData.name && (
                            <p className={styles.error}>{t('support.errors.name_required', 'Name is required.')}</p>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="surname">
                            {t('support.fields.surname_label', 'Surname')}<span className={styles.required}>*</span>:
                        </label>
                        <input
                            type="text"
                            id="surname"
                            name="surname"
                            value={formData.surname}
                            onChange={handleChange}
                            className={styles.input}
                            required
                            placeholder={t('support.fields.surname_placeholder', 'Enter your surname')}
                        />
                        {submitAttempted && !formData.surname && (
                            <p className={styles.error}>{t('support.errors.surname_required', 'Surname is required.')}</p>
                        )}
                    </div>
                </div>

                {/* SECOND ROW: EMAIL AND MOBILE NR */}
                <div className={styles.twoColumnRow}>
                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="email">
                            {t('support.fields.email_label', 'E-mail')}<span className={styles.required}>*</span>:
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={styles.input}
                            required
                            placeholder={t('support.fields.email_placeholder', 'Enter your email')}
                        />
                        {submitAttempted && (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) && (
                            <p className={styles.error}>{t('support.errors.email_invalid', 'Please enter a valid email address.')}</p>
                        )}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="mobile">
                            {t('support.fields.mobile_label', 'Mobile Nr')}<span className={styles.required}>*</span>:
                        </label>
                        <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className={styles.input}
                            required
                            placeholder={t('support.fields.mobile_placeholder', 'Enter your mobile number')}
                        />
                        {submitAttempted && !formData.mobile && (
                            <p className={styles.error}>{t('support.errors.mobile_required', 'Mobile number is required.')}</p>
                        )}
                    </div>
                </div>

                {/* SINGLE COLUMN: ISSUE */}
                <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="issue">
                        {t('support.fields.issue_label', 'Issue')}<span className={styles.required}>*</span>:
                    </label>
                    <select
                        id="issue"
                        name="issue"
                        value={formData.issue}
                        onChange={handleChange}
                        className={styles.select}
                        required
                    >
                        {issueOptions.map((option, idx) => (
                            <option key={idx} value={option.value} disabled={option.value === ''}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {submitAttempted && !formData.issue && (
                        <p className={styles.error}>{t('support.errors.issue_required', 'Please select an issue.')}</p>
                    )}
                </div>

                {/* SINGLE COLUMN: DESCRIPTION */}
                <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="description">
                        {t('support.fields.description_label', 'Description')}<span className={styles.required}>*</span>:
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className={styles.textarea}
                        required
                        placeholder={t('support.fields.description_placeholder', 'Describe your issue in detail...')}
                    ></textarea>
                    {submitAttempted && !formData.description && (
                        <p className={styles.error}>{t('support.errors.description_required', 'Description is required.')}</p>
                    )}
                </div>

                {/* SINGLE COLUMN: CAPTCHA */}
                <div className={styles.formGroup}>
                    <label className={styles.label}>{t('support.fields.captcha_label', 'CAPTCHA')}<span className={styles.required}>*</span>:</label>
                    <div className={styles.captchaContainer}>
                        <span className={styles.captchaQuestion}>{captcha.num1} + {captcha.num2} =</span>
                        <input
                            type="text"
                            name="captchaInput"
                            value={formData.captchaInput}
                            onChange={handleChange}
                            className={`${styles.input} ${styles.captchaInput}`}
                            required
                            pattern="\d*"
                            title={t('support.fields.captcha_title', 'Please enter only numbers.')}
                        />
                    </div>
                    {submitAttempted && captchaError && (
                        <p className={styles.error}>{t('support.errors.captcha_incorrect', 'Incorrect CAPTCHA answer. Please try again.')}</p>
                    )}
                </div>

                <button type="submit" className={styles.button} disabled={isLoading}>
                    {isLoading ? t('support.submitting', 'Submitting...') : t('support.submit_button', 'Submit Request')}
                </button>
            </form>
        </div>
    );
}
